/**
 * Kind-dispatched resolver: `Insight` → `GraphView`.
 *
 *  - cluster      → reuse the existing `pattern-query` pipeline with one
 *                   `componentId` filter.
 *  - top_entity   → fetch the entity's 2-hop neighbourhood via
 *                   `getNodeNeighbors`.
 *  - bridge       → reuse `fetchPatternUnion` with two `componentId` filters
 *                   (componentA + componentB). The union helper unions both
 *                   clusters' nodes and pulls cross-cluster edges via
 *                   `getEdgesBetween`, surfacing the bridge naturally.
 */

import { edgeFromRelationship, makeMeta, nodeFromPerson } from '@/features/graph/utils/builders'
import { graphApi } from '@/services/api/graph'
import { fetchPatternUnion } from '@/services/api/pattern-query-union'
import type { ActiveGraphFilter } from '@/types/graph-query'
import type { GraphView } from '@/types/graph-view'
import { emptyViewState } from '@/types/graph-view'
import type { Insight } from './types'

async function resolveCluster(caseId: string, insight: Insight): Promise<GraphView> {
  if (insight.metadata.kind !== 'cluster') throw new Error('cluster insight metadata kind mismatch')
  const filter: ActiveGraphFilter = {
    id: insight.id,
    componentId: insight.metadata.componentId,
    label: insight.title,
  } as ActiveGraphFilter
  const view = await fetchPatternUnion(caseId, [filter])
  return { ...view, meta: makeMeta(caseId, insight.title) }
}

async function resolveTopEntity(caseId: string, insight: Insight): Promise<GraphView> {
  if (insight.metadata.kind !== 'top_entity')
    throw new Error('top_entity insight metadata kind mismatch')
  const { entityId, entityName, entityType } = insight.metadata
  // 2-hop neighbourhood for top-entity insights — gives more context than
  // 1-hop, which often shows just the entity and a single relation.
  const res = await graphApi.getNodeNeighbors(caseId, entityId, { hops: 2 })
  const nodes = res.nodes.map(nodeFromPerson)
  // Ensure the centre entity exists in the node set even if the neighbours
  // endpoint excluded it (it shouldn't, but defence in depth).
  if (!nodes.some(n => n.id === entityId)) {
    nodes.unshift({
      id: entityId,
      name: entityName,
      labels: [entityType],
      typeLabel: entityType,
      properties: {},
    })
  }
  const edges = res.relationships.map(edgeFromRelationship)
  return {
    meta: makeMeta(caseId, insight.title),
    nodes,
    edges,
    view: emptyViewState(),
  }
}

async function resolveBridge(caseId: string, insight: Insight): Promise<GraphView> {
  if (insight.metadata.kind !== 'bridge') throw new Error('bridge insight metadata kind mismatch')
  const { componentA, componentB } = insight.metadata
  // Reuse the chip pattern-query union: with two `componentId` filters it
  // returns the nodes of both clusters, then `getEdgesBetween` (inside the
  // union helper) brings in all cross-cluster edges — including the bridging
  // one — for free. Same result as the Insights Dashboard's "Cluster bridges"
  // section when both clusters are pinned.
  const filters: ActiveGraphFilter[] = [
    {
      id: `bridge-cluster-${componentA}`,
      componentId: componentA,
      label: `Cluster ${componentA}`,
    },
    {
      id: `bridge-cluster-${componentB}`,
      componentId: componentB,
      label: `Cluster ${componentB}`,
    },
  ] as ActiveGraphFilter[]
  const view = await fetchPatternUnion(caseId, filters)
  return { ...view, meta: makeMeta(caseId, insight.title) }
}

export async function resolveInsight(caseId: string, insight: Insight): Promise<GraphView> {
  switch (insight.kind) {
    case 'cluster':
      return resolveCluster(caseId, insight)
    case 'top_entity':
      return resolveTopEntity(caseId, insight)
    case 'bridge':
      return resolveBridge(caseId, insight)
  }
}
